import { useEffect, useRef } from 'react';
import { useAuth } from './auth-context';

/**
 * Debug hook to monitor auth context changes
 * Use this in components that are experiencing unexpected re-renders
 */
export function useAuthDebug(componentName: string) {
  const auth = useAuth();
  const prevAuth = useRef(auth);
  const renderCount = useRef(0);

  useEffect(() => {
    renderCount.current += 1;
    
    const changes: string[] = [];
    
    if (prevAuth.current.user !== auth.user) {
      changes.push(`user: ${prevAuth.current.user?.id} → ${auth.user?.id}`);
    }
    
    if (prevAuth.current.profile !== auth.profile) {
      changes.push(`profile: ${prevAuth.current.profile?.id} → ${auth.profile?.id}`);
    }
    
    if (prevAuth.current.isLoading !== auth.isLoading) {
      changes.push(`isLoading: ${prevAuth.current.isLoading} → ${auth.isLoading}`);
    }
    
    if (prevAuth.current.isAuthenticated !== auth.isAuthenticated) {
      changes.push(`isAuthenticated: ${prevAuth.current.isAuthenticated} → ${auth.isAuthenticated}`);
    }

    if (changes.length > 0) {
      console.log(`🔄 [${componentName}] Auth context changed (render #${renderCount.current}):`, changes);
    }

    prevAuth.current = auth;
  });

  return {
    renderCount: renderCount.current,
    authState: auth
  };
}

/**
 * Component wrapper to debug auth-related re-renders
 */
export function AuthDebugWrapper({ 
  children, 
  componentName 
}: { 
  children: React.ReactNode; 
  componentName: string; 
}) {
  const { renderCount } = useAuthDebug(componentName);
  
  return (
    <div data-auth-debug={componentName} data-render-count={renderCount}>
      {children}
    </div>
  );
}

/**
 * Higher-order component to wrap components with auth debugging
 */
export function withAuthDebug<P extends object>(
  Component: React.ComponentType<P>,
  componentName: string
) {
  return function AuthDebuggedComponent(props: P) {
    useAuthDebug(componentName);
    return <Component {...props} />;
  };
} 